// 고밀도 장부 표 래퍼 — <table className="v2-ledger"> (th 11px sticky / td 34px / 밴드 tr.band / .num).
// props: minWidth (표의 실제 최소폭 — 이 값으로 '넘침' 을 판정한다), children (thead/tbody 그대로),
//        foot (표 아래 푸터 노드 — 건수·단위·페이지),
//        cols  (L1) [{ w: number|string, align: 'left'|'right'|'center' }] — 열 폭 <colgroup> + th/td 정렬.
//              열이 적은 표가 전폭에 퍼지는 것을 막는다(감사 L1/B2). 생략하면 예전과 100% 동일하게 동작한다.
//        maxWidth (L1) **콘텐츠 열 합계 상한**. 표 자체는 항상 카드 전폭(.v2-ledger width:100%) 이고,
//              남는 폭은 마지막 filler 열(<col width:calc(100% - maxWidth)> + 각 행 끝 빈 th/td)이 흡수한다 —
//              그래야 thead 회색 띠와 행 밑줄이 카드 끝까지 이어진다(FIX1, 표가 카드 중간에서 끊겨 보이던 문제).
//              넘침(hScroll) 판정은 유지 — 가용폭 = min(컨테이너, maxWidth) 이라 그보다 넓은 표는 그 안에서 가로 스크롤된다.
//              filler 는 cols 가 있고 래퍼가 안 붙은(데스크톱 · 안 넘침) 경우에만. cols 없이 maxWidth 만 준 표는 예전대로 표에 maxWidth.
// 래퍼 규칙:
//  - 모바일(≤768): 항상 hScroll 래퍼 + minWidth (실측 룰).
//  - 데스크톱: 컨테이너 폭 < minWidth 일 때만 hScroll 래퍼. Card 가 overflow:clip 이라 래퍼가 없으면
//    19컬럼 표의 오른쪽이 잘려 도달 불가가 된다(1024 실측: 표 1330 / 컨테이너 1009).
//    폭은 ResizeObserver(없으면 window resize) 로 실측 — 사이드바 접기·창 리사이즈에 따라온다.
//  - 넘치지 않으면 래퍼 없음 → .dash-content 가 스크롤 조상이라 th sticky(top:-24)가 헤더 아래 붙는다.
// 카드 안에 둘 때 카드는 overflow:clip (S.card 기본) — hidden 이면 sticky 가 갇힌다.
import React, { useState, useEffect, useRef } from 'react';
import { useIsMobile, hScroll } from '../../utils/useMobile';
import { T, FONT } from '../theme';

var footSt = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', fontSize: 11.5, color: T.muted, borderTop: '1px solid ' + T.bd, flexWrap: 'wrap' };

// cols[i].align 을 th/td 에 얹는다. 셀의 자기 style 이 우선(덮어쓰기 가능), colSpan>1 인 밴드 행은 건너뛴다.
// filler=true 면 각 행 끝에 빈 셀을 하나 더 붙인다 — 마지막 셀이 colSpan 병합(밴드 행)이면 그 colSpan 을 +1.
// 행이 Fragment 로 감싸여 있으면 Children.map 이 안으로 들어가지 못해 그 행만 정렬이 적용되지 않는다(무해).
function alignCells(children, cols, filler) {
  return React.Children.map(children, function (sec) {
    if (!React.isValidElement(sec) || !sec.props) return sec;
    var headCell = sec.type === 'thead' ? 'th' : 'td';
    var rows = React.Children.map(sec.props.children, function (tr) {
      if (!React.isValidElement(tr) || tr.type !== 'tr' || !tr.props) return tr;
      var i = 0;
      var cells = React.Children.map(tr.props.children, function (cell) {
        if (!React.isValidElement(cell) || (cell.type !== 'th' && cell.type !== 'td')) return cell;
        var col = cols[i];
        var span = Number(cell.props.colSpan) || 1;
        i += span;
        if (span > 1 || !col || !col.align) return cell;
        return React.cloneElement(cell, { style: Object.assign({ textAlign: col.align }, cell.props.style) });
      });
      if (!filler) return React.cloneElement(tr, undefined, cells);
      var out = cells ? cells.slice() : [];
      var last = -1;
      for (var k = out.length - 1; k >= 0; k--) {
        if (React.isValidElement(out[k]) && (out[k].type === 'th' || out[k].type === 'td')) { last = k; break; }
      }
      if (last >= 0 && Number(out[last].props.colSpan) > 1) out[last] = React.cloneElement(out[last], { colSpan: Number(out[last].props.colSpan) + 1 });
      else out.push(React.createElement(headCell, { key: 'v2-filler', 'aria-hidden': 'true', className: 'filler' }));
      return React.cloneElement(tr, undefined, out);
    });
    return React.cloneElement(sec, undefined, rows);
  });
}

function Ledger(props) {
  var isMobile = useIsMobile();
  var minWidth = props.minWidth;
  var maxWidth = props.maxWidth;
  var cols = props.cols;
  var tableRef = useRef(null);
  var [over, setOver] = useState(false);

  useEffect(function () {
    // 모바일은 무조건 래퍼라 실측이 필요 없다. minWidth 를 안 준 표는 컨테이너에 맞춰 접히므로 넘칠 것이 없다.
    if (isMobile || !minWidth) { setOver(false); return undefined; }
    var host = tableRef.current && tableRef.current.parentNode;   // 래퍼 삽입 전후 모두 이 노드가 컨테이너 폭 기준
    if (!host) return undefined;
    var measure = function () {
      var w = host.clientWidth;
      var avail = maxWidth ? Math.min(w, Number(maxWidth)) : w;   // maxWidth 로 좁힌 표는 그 폭 안에서 넘침을 판정
      setOver(w > 0 && Number(minWidth) > avail + 1);             // jsdom 은 폭 0 → 항상 false (래퍼 없음)
    };
    measure();
    if (typeof window.ResizeObserver === 'function') {
      var ro = new window.ResizeObserver(measure);
      ro.observe(host);
      return function () { ro.disconnect(); };
    }
    window.addEventListener('resize', measure);
    return function () { window.removeEventListener('resize', measure); };
  }, [isMobile, minWidth, maxWidth]);

  var wrap = isMobile || over;                                   // 모바일은 minWidth 유무와 무관하게 래퍼 유지(기존 동작)
  var hasCols = !!(cols && cols.length);
  // 표는 카드 전폭 그대로 두고 (maxWidth - 컨테이너) 만큼을 filler 열이 먹는다. 컨테이너가 maxWidth 보다 좁으면 calc 이 음수 → 0.
  var filler = !!(maxWidth && hasCols && !wrap);
  var tableSt = wrap && minWidth ? { minWidth: minWidth } : undefined;
  if (maxWidth && !wrap && !filler) tableSt = Object.assign({}, tableSt, { maxWidth: maxWidth });
  var colgroup = hasCols ? (<colgroup>
    {cols.map(function (c, i) { return <col key={i} style={c && c.w ? { width: c.w } : undefined} />; })}
    {filler ? <col key="v2-filler" style={{ width: 'calc(100% - ' + Number(maxWidth) + 'px)' }} /> : null}
  </colgroup>) : null;
  var body = hasCols ? alignCells(props.children, cols, filler) : props.children;
  var table = (<table ref={tableRef} className="v2-ledger" style={tableSt}>{colgroup}{body}</table>);
  var wrapSt = maxWidth ? Object.assign({}, hScroll, { maxWidth: maxWidth }) : hScroll;
  return (<React.Fragment>
    {wrap ? <div style={wrapSt}>{table}</div> : table}
    {props.foot ? <div className="v2-tfoot" style={Object.assign({}, footSt, { fontSize: isMobile ? FONT.body : 11.5 })}>{props.foot}</div> : null}
  </React.Fragment>);
}

export default Ledger;
